import styled from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { NoSelectionOverlay } from '../card/noSelectionOverlay';
import { AmChart, coreTheme } from './amChart';
import { addHoverAnimation } from './onHoverAnimation';
import { ExportButton } from '../controls/exportButton';
import { addLegend, addLink, handleChartPlotContainerHitEvents } from './lib';
import { Theme } from '../../containers/app/themes';

import { setupEventOverlaysSeries } from '../../containers/core/device/compositions/performance/charts/lib/eventOverlays';
import { unitOfTime } from 'moment';
import { animateBar } from './animateBar';
import './columnDateChart.css';
import { isEqual } from 'lodash';

/* istanbul ignore next */
const Container = styled.div<{ bottomBorder: boolean }>`
  position: relative;
  margin-top: 1.5rem;
  ${({ bottomBorder }) => bottomBorder && `border-bottom: 3px solid #edf1f3;`}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-weight: ${({ theme }: { theme: Theme }) => theme.font.weights.bold};
  font-size: ${({ theme }: { theme: Theme }) => theme.font.sizes.sixteenPixels};
  width: 65%;
  margin-bottom: 0;
`;

const ChartContainer = styled.div<{ length?: number }>`
  width: 95%;
  margin-left: auto;
  margin-right: auto;
  height: 14rem;
  ${({ length }) => length && `width: ${length}%;`}
`;

const NoDataOverlay = styled(NoSelectionOverlay)`
  padding: 0;
`;

const Controls = styled.div`
  margin-right: 0.625rem;
  display: flex;

  & button {
    margin-bottom: 5px;
  }
`;

type LinePattern = Pick<
  am4core.LinePattern,
  "width" | "height" | "strokeWidth" | "stroke" | "rotation"
>;

export type XAxisTypes = 'date' | 'category';
export type YAxisTypes = 'value' | 'duration';

export interface StackedColumnsChartSeries<Data extends Record<string, any>> {
  visible: boolean,
  dataKey: keyof Omit<Data, 'date'>,
  description: string,
  colour: string,
  animate?: (column: any) => boolean,
  linePattern?: LinePattern,
  /** Overrides the legend's own label for this series, independent of `description` (which still
   * drives every tooltip row - see tooltips.tsx). Falls back to `description` when omitted, so
   * every existing caller's legend is unaffected. */
  legendLabel?: string,
  /** Hides this series from the legend entirely, regardless of `visible` - e.g. so two series that
   * share one combined `legendLabel` (see deviceAnomaliesDataUsage.tsx) only contribute a single
   * legend row between them. The series itself still renders in the chart and still appears in
   * the tooltip. Defaults to false for every existing caller. */
  hideFromLegend?: boolean
}

export interface dateAxisEndDuration {
  amount: string,
  unit : unitOfTime.DurationConstructor
}

export interface BaseStackedColumnsChartProps<Data extends Record<string, any>> {
  data: Data[] | null,
  className?: string,
  series: StackedColumnsChartSeries<Data>[],
  onBarClick?: (event: any) => void,
  onBarDblClick?: (event: any) => void,
  title: string | JSX.Element,
  days?: number,
  cypressId: string,
  bottomBorder?: boolean,
  onMount?: (chart: am4charts.XYChart) => am4charts.XYChart,
  headerControls?: JSX.Element,
  buttons?: {
    Component: (props: any) => JSX.Element,
    props: any
  }[],
  export?: {
    onExportClicked: () => Promise<string>,
    filename: string,
    csv: boolean
  },
  drawYAxis?: (chart: am4charts.XYChart, axis: am4charts.ValueAxis | am4charts.DurationAxis, enabledSeries?: any[]) => void,
  yAxisType?: 'value' | 'duration',
  xAxisType?: XAxisTypes,
  link?: string,
  hasLegend: boolean,
  drawXAxis: (chart: am4charts.XYChart, xAxis: am4charts.CategoryAxis | am4charts.DateAxis) => void,
  columnWidth?: number,
  hasNoSelectionOverlay?: boolean,
  getTooltipAdapter?: ({ seriesName }: { seriesName: string }) => (_: any, target: any) => string,
  useAxisTooltip?: boolean,
  getAxisTooltipAdapter?: () => (_: any, target: any) => string,
  eventOverlay?: boolean,
  displayTimeZone?: string,
  dateAxisEndDuration?: dateAxisEndDuration,
  adjustChartDimensions?: {length?: number, height: number},
  dragToZoom?: boolean,
  eventTickLength?: number
}

export interface StackedColumnChartProps<Data extends Record<string, any>>
  extends BaseStackedColumnsChartProps<Data> {
  xAxisType: XAxisTypes,
  xFieldType: 'categoryX' | 'dateX',
  categoryKey: keyof Data,
  provideXAxis: (chart: am4charts.XYChart) => am4charts.CategoryAxis | am4charts.DateAxis,
  provideYAxis: (chart: am4charts.XYChart) => am4charts.ValueAxis | am4charts.DurationAxis
}

export function StackedColumnsChart<Data extends Record<string, any>>(props: StackedColumnChartProps<Data>) {
  const [chartReady, setChartReady] = useState(false);
  const chartRef = useRef(null);
  const ns = 'translation';
  const { t } = useTranslation(['performance', 'editEvents', 'translation', 'deviceApplications', ns, 'timeState']);
  const { series: allSeries, drawYAxis, drawXAxis } = props;

  // amCharts registers the plot container's hit/doublehit listeners once, when the chart is
  // created. Callers whose onBarClick/onBarDblClick closes over state that changes without
  // remounting the chart (e.g. a series/kind toggle) would otherwise have every click handled by
  // the stale, mount-time closure. Keeping the latest handlers in refs and registering stable
  // wrappers that delegate to them ensures clicks always run the current handler.
  const onBarClickRef = useRef(props.onBarClick);
  onBarClickRef.current = props.onBarClick;
  const onBarDblClickRef = useRef(props.onBarDblClick);
  onBarDblClickRef.current = props.onBarDblClick;

  useEffect(() => {
    allSeries.forEach((seriesData) => {
      const series = chartRef.current.map.getKey(seriesData.dataKey);
      if (seriesData.visible) {
        series.show();
        series.hiddenInLegend = Boolean(seriesData.hideFromLegend);
      } else {
        series.hide();
        series.hiddenInLegend = true;
      }

      if (seriesData.animate) {
        series.columns.each((column: am4charts.Column) => {
          if (seriesData.animate(column)) {
            animateBar(column);
          }
        });
      }

      if (!props.useAxisTooltip) {
        series.columns.template.dummyData.allSeries = allSeries;
      }
    });
    drawYAxis && drawYAxis(chartRef.current, chartRef.current?.dummyData?.yAxis, allSeries.filter(series => series.visible).map(series => series.dataKey) as string[]);

    const xAxisData = chartRef.current?.dummyData?.xAxis.dummyData;
    if (xAxisData) {
      xAxisData.allSeries = allSeries;
    }
  }, [allSeries, drawYAxis, props.data, props.useAxisTooltip, chartRef.current?.dummyData?.yAxis]);

  return (
    <Container className={props.className || ''} bottomBorder={props.bottomBorder} data-id={`${props.cypressId}${chartReady ? '--chart-ready' : ''}`}>
      <Header className="header">
        {props.hasNoSelectionOverlay !== false && <NoDataOverlay noSelectionText={t('NO_DATA_AVAILABLE', { ns })} show={Boolean(!props.data || props.data.length === 0)} />}
        <Title>{props.title}</Title>
        {(props.headerControls || props.buttons || props.export) && <Controls>
          {props.headerControls}
          {props.buttons?.map((button, index) => {
            return <button.Component key={`list-header-button-${index}`} {...button.props} />;
          })}
          {props.export && <ExportButton exportFunction={props.export.onExportClicked} filename={props.export.filename} />}
        </Controls>}
      </Header>
      <ChartContainer length={props?.adjustChartDimensions?.length}>
        <AmChart
          tag={props.cypressId}
          chartProvider={createChart}
          dataProvider={() => props.data}
          onUpdate={props?.eventOverlay ? onDataUpdated : null}
          onMount={onMount}
          onDataValidated={() => setChartReady(true)}
          link={props.data}
          cypressId={props.cypressId}
        />
      </ChartContainer>
    </Container>
  );

  function createChart(id: string) {
    am4core.useTheme(coreTheme);

    const chart = am4core.create(id, am4charts.XYChart);
    chart.paddingTop = props.eventOverlay ? 40 : 20;
    chart.minHeight = props?.adjustChartDimensions?.height ?? 220;

    function createSeries(field: keyof Data, name: string, colour: string, xAxis: any, linePattern?: LinePattern, firstSeries?: boolean, hideFromLegend?: boolean) {
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.id = field as unknown as string;
      series.name = name;
      if (hideFromLegend) {
        series.hiddenInLegend = true;
      }
      series.dataFields.valueY = field as unknown as string;
      series.dataFields[`${props.xFieldType}`] = props.categoryKey as string;
      series.sequencedInterpolation = true;
      series.stacked = true;

      series.columns.template.fill = am4core.color(colour);
      series.columns.template.stroke = am4core.color(colour);
      series.columns.template.width = am4core.percent(props.columnWidth || 90);
      series.columns.template.tooltipHTML = '';

      if (firstSeries) {
        // Only register a listener when a handler was actually provided at creation time (mirrors
        // the previous behaviour, where handleChartPlotContainerHitEvents itself no-ops when the
        // handler is falsy). The wrapper still delegates to the ref so it keeps calling whichever
        // handler is current, even though amCharts only ever calls chartProvider once.
        if (props.onBarClick) {
          handleChartPlotContainerHitEvents('hit', (data: any) => onBarClickRef.current?.(data), chart, series, xAxis);
        }
        if (props.onBarDblClick) {
          handleChartPlotContainerHitEvents('doublehit', (data: any) => onBarDblClickRef.current?.(data), chart, series, xAxis);
        }
      }

      /* istanbul ignore next */
      if (props.onBarClick || props.onBarDblClick) {
        chart.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      }

      if (props.link) {
        addLink(series, props.link);
      }
      if (!props.useAxisTooltip) {
        series.columns.template.adapter.add("tooltipHTML", props.getTooltipAdapter({ seriesName: field as unknown as string }));
        series.columns.template.dummyData = {
          allSeries: props.series
        };
      }

      series.dummyData = {
        colour: am4core.color(colour)
      };

      addHoverAnimation(series, chart);

      if (linePattern) {
        series.columns.template.adapter.add("fill", () => {
          const pattern = new am4core.LinePattern();

          pattern.width = linePattern.width;
          pattern.height = linePattern.height;
          pattern.strokeWidth = linePattern.strokeWidth;
          pattern.stroke = linePattern.stroke;
          pattern.rotation = linePattern.rotation;

          return pattern;
        });

        series.columns.template.strokeWidth = 0;
      }

      return series;
    }

    const xAxis = props.provideXAxis(chart);
    const yAxis = props.provideYAxis(chart);

    props.series.forEach((series, i) => {
      createSeries(series.dataKey, series.legendLabel ?? series.description, series.colour, xAxis, series.linePattern, i === 0, series.hideFromLegend);
    });
    setupEventOverlaysSeries(props.eventOverlay, t, chart, props.xAxisType, props.dateAxisEndDuration, props.eventTickLength, props.displayTimeZone);

    chart.zoomOutButton.disabled = !!props.dragToZoom;
    chart.dummyData = {
      yAxis,
      xAxis
    };

    if (props.hasLegend) {
      addLegend(chart);
    }

    chartRef.current = chart;
    return chart;
  }

  /* istanbul ignore next */
  function onDataUpdated(chart: am4charts.XYChart, chartData: any[], prevProps?: any, currentProps?: any) {
    const currentPropsEvents = extractEventsFromData(currentProps.link);
    const previousPropsEvents = extractEventsFromData(prevProps.link);
    if (!isEqual(currentPropsEvents, previousPropsEvents)) {
      setupEventOverlaysSeries(props.eventOverlay, t, chart, props.xAxisType, props.dateAxisEndDuration, props.eventTickLength, props.displayTimeZone);
    }
    drawXAxis(chart, chart.xAxes.values[0] as am4charts.CategoryAxis | am4charts.DateAxis);
    return chart;
  }

  function onMount(chart: am4charts.XYChart) {
    props.drawXAxis(chart, chart.xAxes.values[0] as am4charts.CategoryAxis | am4charts.DateAxis);
    props.onMount && props.onMount(chart);
    return chart;
  }

  function extractEventsFromData(data: any) : any[] {
    const events = data.filter((data: any) => data.events);
    return events;
  }
}
